import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { SidebarProvider, FloatingChatProvider, useFloatingChat } from './components/Sidebar';
import { ListenProvider } from './contexts/ListenContext';
import ApprovalPanel from './components/ApprovalPanel';
import ListenWidget from './components/ListenWidget';
import { OrgProvider } from './contexts/OrgContext';
import { VocabularyProvider } from './vocabulary/VocabularyProvider';
import { WorkAreaProvider } from './contexts/WorkAreaContext';
import OrgSelector from './components/OrgSelector';

// Pages
import Landing from './pages/Landing';
import Dashboard from './pages/Dashboard';
import DailyDashboard2 from './pages/DailyDashboard2';
import DataView from './pages/DataView';
import TasksPage from './pages/TasksPage';
import Settings from './pages/Settings';
import EODPage from './pages/EODPage';
import AnalyticsPage from './pages/AnalyticsPage';
import AIPage from './pages/AIPage';
import SwitchboardPage from './pages/SwitchboardPage';
import OrganisationPage from './pages/OrganisationPage';
import BigPicturePage from './pages/BigPicturePage';
import FinancePage from './pages/FinancePage';
import ExpenseCapturePage from './pages/ExpenseCapturePage';
import MobileCapturePage from './pages/MobileCapturePage';
import FloatingChat from './components/dashboard/FloatingChat';
import RecruitmentPage from './pages/recruitment/RecruitmentPage';
import TestingLabPage from './pages/TestingLabPage';
import TestingLabRunnerPage from './pages/TestingLabRunnerPage';
import BillingPage from './pages/BillingPage';
import AdminIdentityPage from './pages/AdminIdentityPage';
import AdminAuditPage from './pages/AdminAuditPage';
import OwnerConsoleLayout from './pages/owner/OwnerConsoleLayout';
import OwnerOverviewPage from './pages/owner/OwnerOverviewPage';
import OwnerBootstrapLoginPage from './pages/OwnerBootstrapLoginPage';
import SuperAdminPage from './pages/SuperAdminPage';
import OwnerAccountsListPage from './pages/owner/OwnerAccountsListPage';
import OwnerAccountDetailPage from './pages/owner/OwnerAccountDetailPage';
import OwnerPeoplePage from './pages/owner/OwnerPeoplePage';
import OwnerPersonDetailPage from './pages/owner/OwnerPersonDetailPage';
import OwnerWebsitePage from './pages/owner/OwnerWebsitePage';
import DesignSystemPreviewPage from './pages/design-system/DesignSystemPreviewPage';
import V3AuditBanner from './components/design-system/V3AuditBanner';
import PublicSitePage from './pages/site/PublicSitePage';
import TestThisNowWidget from './components/testing_lab/TestThisNowWidget';

// Marketing Pages (legacy v1 — superseded by CMS-driven /site/*)
// Kept for backwards compatibility on /landing redirect chain.

const API = '/api';

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

// Theme Context
const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
};

export const DENSITY_OPTIONS = ['compact', 'standard', 'calm'];

// Theme Provider
const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const stored = localStorage.getItem('orka-theme');
    if (stored) return stored;
    return 'system';
  });
  const [density, setDensity] = useState(() => {
    const stored = localStorage.getItem('orka-density');
    if (stored && DENSITY_OPTIONS.includes(stored)) return stored;
    return 'standard';
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'system') {
      const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      root.classList.toggle('dark', systemDark);
    } else {
      root.classList.toggle('dark', theme === 'dark');
    }
    localStorage.setItem('orka-theme', theme);
  }, [theme]);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-density', density);
    localStorage.setItem('orka-density', density);
  }, [density]);

  // Listen for system theme changes
  useEffect(() => {
    if (theme !== 'system') return;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => {
      document.documentElement.classList.toggle('dark', e.matches);
    };
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, density, setDensity }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Auth helpers - store token in localStorage as fallback when cookies fail (Cloudflare/proxy)
const AUTH_TOKEN_KEY = 'orka_session_token';

export const getAuthHeaders = () => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

export const authFetch = (url, options = {}) => {
  const headers = { ...getAuthHeaders(), ...(options.headers || {}) };
  return fetch(url, { ...options, headers, credentials: 'include' });
};

// Global fetch interceptor: auto-inject auth token for API calls
const _originalFetch = window.fetch;
window.fetch = function(url, options = {}) {
  if (typeof url === 'string' && url.includes('/api/')) {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token) {
      const existingHeaders = options.headers || {};
      // Convert Headers object to plain object if needed
      let plainHeaders = {};
      if (existingHeaders instanceof Headers) {
        existingHeaders.forEach((v, k) => { plainHeaders[k] = v; });
      } else {
        plainHeaders = { ...existingHeaders };
      }
      if (!plainHeaders['Authorization'] && !plainHeaders['authorization']) {
        plainHeaders['Authorization'] = `Bearer ${token}`;
      }
      options = { ...options, headers: plainHeaders, credentials: 'include' };
    }
  }
  return _originalFetch.call(this, url, options);
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actingAs, setActingAs] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const response = await authFetch(`${API}/auth/me`);
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
        localStorage.removeItem(AUTH_TOKEN_KEY);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes('session_id=') ||
        window.location.pathname === '/auth/callback') {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `${API}/auth/google/login?redirect_uri=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await authFetch(`${API}/auth/logout`, { method: 'POST' });
    } catch (e) {
      console.error('Logout error:', e);
    }
    localStorage.removeItem(AUTH_TOKEN_KEY);
    setUser(null);
    setActingAs(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, checkAuth, actingAs, setActingAs }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser, checkAuth } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Check for Google OAuth token in query params
      const params = new URLSearchParams(window.location.search);
      const token = params.get('token');
      const redirect = params.get('redirect') || '/dashboard';

      if (token) {
        // Store token in localStorage for auth header fallback
        localStorage.setItem(AUTH_TOKEN_KEY, token);
        try {
          const res = await fetch(`${API}/auth/exchange-token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            credentials: 'include',
            body: JSON.stringify({ token }),
          });
          if (res.ok) {
            const userData = await res.json();
            setUser(userData);
            let path = '/dashboard';
            try { const url = new URL(redirect); path = url.pathname; }
            catch { path = redirect.startsWith('/') ? redirect : '/dashboard'; }
            toast.success(`Welcome, ${userData.name || 'back'}!`);
            navigate(path, { replace: true });
          } else {
            localStorage.removeItem(AUTH_TOKEN_KEY);
            toast.error('Authentication failed');
            navigate('/');
          }
        } catch (err) {
          console.error('Token exchange error:', err);
          localStorage.removeItem(AUTH_TOKEN_KEY);
          toast.error('Authentication error');
          navigate('/');
        }
        return;
      }

      // Check for Emergent Auth session_id in hash
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        navigate('/');
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        const response = await fetch(`${API}/auth/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId })
        });

        if (response.ok) {
          const userData = await response.json();
          // Store session token in localStorage for Bearer auth fallback
          if (userData.session_token) {
            localStorage.setItem(AUTH_TOKEN_KEY, userData.session_token);
          }
          setUser(userData);
          toast.success(`Welcome, ${userData.name}!`);
          navigate('/dashboard', { replace: true, state: { user: userData } });
        } else {
          toast.error('Authentication failed');
          navigate('/');
        }
      } catch (error) {
        console.error('Auth error:', error);
        toast.error('Authentication error');
        navigate('/');
      }
    };

    processAuth();
  }, [navigate, setUser, checkAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto mb-4"></div>
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (!user && !location.state?.user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// App Router
// Smart Landing - redirect to dashboard if logged in, show new CMS-driven marketing site if not
const SmartLanding = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div></div>;
  if (user) return <Navigate to="/dashboard" replace />;
  return <PublicSitePage />;
};

const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<SmartLanding />} />
      <Route path="/landing" element={<Navigate to="/site/home" replace />} />
      <Route path="/features" element={<Navigate to="/site/how-it-works" replace />} />
      <Route path="/for-ceos" element={<Navigate to="/site/use-cases/founders" replace />} />
      <Route path="/pricing" element={<Navigate to="/site/pricing" replace />} />
      {/* CMS-driven public marketing site */}
      <Route path="/site" element={<Navigate to="/site/home" replace />} />
      <Route path="/site/*" element={<PublicSitePage />} />
      <Route path="/owner-login" element={<OwnerBootstrapLoginPage />} />
      <Route path="/superadmin" element={<SuperAdminPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/dashboard-2" element={
        <ProtectedRoute>
          <DailyDashboard2 />
        </ProtectedRoute>
      } />
      <Route path="/data" element={
        <ProtectedRoute>
          <TasksPage />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Settings />
        </ProtectedRoute>
      } />
      <Route path="/eod" element={
        <ProtectedRoute>
          <EODPage />
        </ProtectedRoute>
      } />
      <Route path="/analytics" element={
        <ProtectedRoute>
          <AnalyticsPage />
        </ProtectedRoute>
      } />
      <Route path="/ai" element={
        <ProtectedRoute>
          <AIPage />
        </ProtectedRoute>
      } />
      <Route path="/switchboard" element={
        <ProtectedRoute>
          <SwitchboardPage />
        </ProtectedRoute>
      } />
      <Route path="/organisation" element={
        <ProtectedRoute>
          <OrganisationPage />
        </ProtectedRoute>
      } />
      <Route path="/big-picture" element={
        <ProtectedRoute>
          <BigPicturePage />
        </ProtectedRoute>
      } />
      <Route path="/finance" element={
        <ProtectedRoute>
          <FinancePage />
        </ProtectedRoute>
      } />
      <Route path="/expense-capture" element={
        <ProtectedRoute>
          <ExpenseCapturePage />
        </ProtectedRoute>
      } />
      <Route path="/recruitment" element={
        <ProtectedRoute>
          <RecruitmentPage />
        </ProtectedRoute>
      } />
      <Route path="/capture" element={
        <ProtectedRoute>
          <MobileCapturePage />
        </ProtectedRoute>
      } />
      <Route path="/testing-lab" element={
        <ProtectedRoute>
          <TestingLabPage />
        </ProtectedRoute>
      } />
      <Route path="/testing-lab/run/:session_id" element={
        <ProtectedRoute>
          <TestingLabRunnerPage />
        </ProtectedRoute>
      } />
      <Route path="/billing" element={
        <ProtectedRoute>
          <BillingPage />
        </ProtectedRoute>
      } />
      <Route path="/billing/success" element={
        <ProtectedRoute>
          <BillingPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/identity" element={
        <ProtectedRoute>
          <AdminIdentityPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/audit" element={
        <ProtectedRoute>
          <AdminAuditPage />
        </ProtectedRoute>
      } />
      {/* Orka Owner Console (PRD: orka_owner_console_prd v0.1) */}
      <Route path="/owner" element={<ProtectedRoute><OwnerConsoleLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<OwnerOverviewPage />} />
        <Route path="accounts" element={<OwnerAccountsListPage />} />
        <Route path="accounts/:account_id" element={<OwnerAccountDetailPage />} />
        <Route path="people" element={<OwnerPeoplePage />} />
        <Route path="people/:person_id" element={<OwnerPersonDetailPage />} />
        <Route path="website" element={<OwnerWebsitePage />} />
        <Route path="design-system" element={<DesignSystemPreviewPage />} />
      </Route>
      {/* V3 Design System preview (Phase 1 acceptance) */}
      <Route path="/design-system" element={<DesignSystemPreviewPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <OrgProvider>
          <VocabularyProvider>
          <WorkAreaProvider>
          <FloatingChatProvider>
            <SidebarProvider>
              <BrowserRouter>
                <ListenProvider>
                  <AppRouter />
                  <OrgSelector />
                  <Toaster position="bottom-right" />
                  <FloatingChatWrapper />
                  <ApprovalPanel />
                  <ListenWidgetWrapper />
                  <TestThisNowWidget />
                  <V3AuditBanner />
                </ListenProvider>
              </BrowserRouter>
            </SidebarProvider>
          </FloatingChatProvider>
          </WorkAreaProvider>
          </VocabularyProvider>
        </OrgProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

// Wrapper to conditionally show FloatingChat based on auth state and FloatingChatContext
const FloatingChatWrapper = () => {
  const location = window.location.pathname;
  const { isOpen, setIsOpen } = useFloatingChat();
  
  // Don't show on landing page, AI page, mobile capture page, or testing lab
  if (location === '/' || location === '/ai' || location === '/capture' || location === '/testing-lab') {
    return null;
  }
  
  return (
    <FloatingChat
      isMinimized={!isOpen}
      onToggleMinimize={() => setIsOpen(!isOpen)}
      onClose={() => setIsOpen(false)}
    />
  );
};

// Wrapper to show ListenWidget only when authenticated
const ListenWidgetWrapper = () => {
  const location = window.location.pathname;
  if (location === '/' || location === '/capture') return null;
  return <ListenWidget />;
};

export default App;
